import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { qk } from '@/lib/queryKeys';

export type Period = {
  id: string;
  branch_id: string;
  start_date: string;
  end_date: string;
  constraints_deadline: string;
  status: 'collecting' | 'drafting' | 'published' | 'archived';
  published_at: string | null;
  /**
   * מתי לפרסם אוטומטית. null = בלי תזמון.
   *
   * אופציונלי כי הוא נוסף במיגרציה 077, ותשובה משרת שעוד לא נפרס
   * לא תכלול אותו.
   */
  publish_at?: string | null;
};

export type ShiftType = {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  color: string | null;
};

export type ConstraintType = {
  id: string;
  name: string;
  is_blocking: boolean;
  color: string | null;
  sort_order: number;
};

export type MyConstraint = {
  id: string;
  date: string;
  shift_type_id: string | null;
  constraint_type_id: string;
};

/** התקופה הפתוחה להגשה בסניף של העובד, אם יש. */
export function useOpenPeriod() {
  return useQuery({
    queryKey: ['open-period'],
    queryFn: async (): Promise<Period | null> => {
      const { data, error } = await supabase
        .from('schedule_periods')
        .select('id, branch_id, start_date, end_date, constraints_deadline, status, published_at')
        .eq('status', 'collecting')
        .order('constraints_deadline')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as Period | null) ?? null;
    },
  });
}

export function usePeriod(periodId: string) {
  return useQuery({
    queryKey: qk.period(periodId),
    enabled: periodId !== '',
    queryFn: async (): Promise<Period> => {
      const { data, error } = await supabase
        .from('schedule_periods')
        .select('id, branch_id, start_date, end_date, constraints_deadline, status, published_at')
        .eq('id', periodId)
        .single();
      if (error) throw error;
      return data as Period;
    },
  });
}

/** סוגי המשמרות של הסניף, או הכלליים. */
export function useShiftTypes(branchId: string | undefined) {
  return useQuery({
    queryKey: ['shift-types', branchId],
    enabled: Boolean(branchId),
    queryFn: async (): Promise<ShiftType[]> => {
      const { data, error } = await supabase
        .from('shift_types')
        .select('id, name, start_time, end_time, color, branch_id')
        .eq('is_active', true)
        .or(`branch_id.is.null,branch_id.eq.${branchId}`)
        .order('sort_order')
        .order('start_time');
      if (error) throw error;
      return (data ?? []) as ShiftType[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useConstraintTypes() {
  return useQuery({
    queryKey: ['constraint-types'],
    queryFn: async (): Promise<ConstraintType[]> => {
      const { data, error } = await supabase
        .from('constraint_types')
        .select('id, name, is_blocking, color, sort_order')
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as ConstraintType[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyConstraints(periodId: string) {
  return useQuery({
    queryKey: qk.constraints(periodId),
    enabled: periodId !== '',
    queryFn: async (): Promise<MyConstraint[]> => {
      const { data, error } = await supabase
        .from('employee_constraints')
        .select('id, date, shift_type_id, constraint_type_id')
        .eq('period_id', periodId);
      if (error) throw error;
      return (data ?? []) as MyConstraint[];
    },
  });
}

export function useMySubmission(periodId: string) {
  return useQuery({
    queryKey: ['submission', periodId],
    enabled: periodId !== '',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('constraint_submissions')
        .select('submitted_at, general_note')
        .eq('period_id', periodId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useConstraintMutations(periodId: string, employeeId: string | undefined) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.constraints(periodId) });

  /** לחיצה על תא קובעת סוג, מחליפה סוג, או מנקה. */
  const setCell = useMutation({
    mutationFn: async (input: {
      date: string;
      shiftTypeId: string;
      existingId: string | null;
      constraintTypeId: string | null;
    }) => {
      if (!employeeId) throw new Error('no employee');

      if (input.constraintTypeId === null) {
        if (!input.existingId) return;
        const { error } = await supabase
          .from('employee_constraints')
          .delete()
          .eq('id', input.existingId);
        if (error) throw error;
        return;
      }

      if (input.existingId) {
        const { error } = await supabase
          .from('employee_constraints')
          .update({
            constraint_type_id: input.constraintTypeId,
            updated_at: new Date().toISOString(),
          })
          .eq('id', input.existingId);
        if (error) throw error;
        return;
      }

      const { error } = await supabase.from('employee_constraints').insert({
        period_id: periodId,
        employee_id: employeeId,
        date: input.date,
        shift_type_id: input.shiftTypeId,
        constraint_type_id: input.constraintTypeId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const submit = useMutation({
    mutationFn: async (note: string) => {
      const { error } = await supabase.rpc('submit_constraints', {
        p_period: periodId,
        p_note: note || null,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['submission', periodId] }),
  });

  return { setCell, submit };
}

/** סגירה או הערה על יום, כפי שהעובד רואה אותה. */
export type Closure = {
  date: string;
  shift_type_id: string | null;
  is_closed: boolean;
  label: string;
  note: string | null;
};

/**
 * הסגירות שחלות על הסניף בטווח. העובד רואה אותן כדי שיֵדע למה יום
 * חסר מהסידור, ולא יגיש אילוץ ליום שלא קיים.
 */
export function useClosures(branchId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: ['closures', branchId, from, to],
    enabled: Boolean(branchId) && from !== '' && to !== '',
    queryFn: async (): Promise<Closure[]> => {
      const { data, error } = await supabase.rpc('closures_for', {
        p_branch: branchId as string,
        p_from: from,
        p_to: to,
      } as never);
      if (error) throw error;
      return (data ?? []) as unknown as Closure[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * הסגירה שחלה על תא. סגירה של כל היום גוברת על סגירה של משמרת אחת,
 * כי אם היום סגור אין מה לדבר על משמרת בתוכו.
 */
export function findClosure(
  closures: Closure[] | undefined,
  date: string,
  shiftTypeId: string | null,
): Closure | undefined {
  if (!closures) return undefined;
  const onDate = closures.filter((c) => c.date === date);
  return (
    onDate.find((c) => c.shift_type_id === null) ??
    (shiftTypeId === null ? undefined : onDate.find((c) => c.shift_type_id === shiftTypeId))
  );
}
